import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { IconButton, TableCell, TableRow } from '@mui/material';
import { onAutocompleteChangeInForm } from '@sharedCR/common/form';
import { AutocompleteCDS, DataFieldCDS, TextFieldCDS } from '@sharedCR/coreDesignSystem/form';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import { DeleteIcon } from 'app/shared/componentsRestyled/coreDesignSystem/icons';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { COMPOSED_CLAIMS } from 'constants/composedClaims';
import { IconSize } from 'constants/coreDesignSystem';
import { NUMBER_MONTHS_IN_YEAR } from 'constants/date';
import { FieldInputType } from 'constants/fields';
import { monthsWithZeroOptions } from 'constants/options';
import { PendingChangesActionType } from 'constants/pendingChanges';
import { getIsFeeAmountEditingDisallowed, isFieldValueChanged } from 'utils/loanFees';
import { roundToDecimals } from 'utils/number';

import { selectIsClaimAllowed } from 'store/auth/selectors';
import {
  selectFormHiddenFieldsMap,
  selectFormLockedFieldsMap,
  selectFormLockedPage,
} from 'store/loanApplications/selectors';
import { selectFormHiddenPage } from 'store/loanApplications/selectors/lockedFields.selectors';
import { selectFeeProviderOptions, selectFeeTypes } from 'store/system/selectors';

import { LoanFeesTestIds } from '../../../../../../../../LoanFees.testIds';
import AprIndicator from '../../../../../AprIndicator';
import { getExtraFeeProps } from '../PrepaidTable.utils';

const feeNameInputProps = {
  classes: {
    input: 'truncate',
  },
};

const PrepaidTableRow = ({
  isLoading,
  vldErrors,
  feeId,
  index,
  handleChange,
  validateField,
  setInForm,
  setForm,
  validateForm,
  filteredLoanFeesOptions,
  onDeleteFee,
  ...fee
}) => {
  const feeProviderOptions = useSelector(selectFeeProviderOptions);
  const feeTypes = useSelector(selectFeeTypes);
  const lockedFieldsMap = useSelector(selectFormLockedFieldsMap(FormNames.FEES));
  const hiddenFieldsMap = useSelector(selectFormHiddenFieldsMap(FormNames.FEES));
  const isPageLocked = useSelector(selectFormLockedPage(FormNames.FEES));
  const isPageHidden = useSelector(selectFormHiddenPage(FormNames.FEES));

  const isEditFeeProviderNameAllowed = useSelector(
    selectIsClaimAllowed(COMPOSED_CLAIMS.LOAN_EDITOR_EDIT_FEE_PROVIDER_NAME),
  );
  const isManageDiscountPointsAllowed = useSelector(
    selectIsClaimAllowed(COMPOSED_CLAIMS.LOAN_EDITOR_MANAGE_DISCOUNT_POINTS),
  );

  const isDisabledByLock = isLoading || isPageLocked;

  const { value: feeTypeIdValue, countTowardsApr } = fee.feeTypeId || {};
  const isAmountEditingDisallowed = getIsFeeAmountEditingDisallowed(feeTypeIdValue, feeTypes);
  const isAnnualAmountDisabled =
    isDisabledByLock || isAmountEditingDisallowed || lockedFieldsMap.annualAmount;

  const { onFilterOptions } = getExtraFeeProps({
    ...fee,
    isManageDiscountPointsAllowed,
  });

  const onFeeTypeNameChange = useCallback(
    index => (e, value) => {
      onAutocompleteChangeInForm({ validateField, setInForm })(e, value);

      setForm(prev => {
        const clonePrev = _.cloneDeep(prev);
        clonePrev.set_prepaids[index].feeTypeName = value?.name;
        clonePrev.set_prepaids[index].feeProviderId = null;

        return clonePrev;
      });
    },
    [setForm, setInForm, validateField],
  );

  const onChangeAutocomplete = useCallback(
    (e, value) => {
      if (_.includes(e.target.id, 'months')) {
        const index = e.target.id.match(/\d+/)[0];

        const totalCollectedValue =
          ((fee.annualAmount ?? 0) * (value?.value ?? 0)) / NUMBER_MONTHS_IN_YEAR;

        setInForm(`set_prepaids[${index}].totalCollected`, roundToDecimals(totalCollectedValue));
      }

      onAutocompleteChangeInForm({ setInForm, validateField })(e, value);
    },
    [fee.annualAmount, setInForm, validateField],
  );

  const onChangeAmountInput = useCallback(
    async (e, validationContext) => {
      if (_.endsWith(e.target.name, 'annualAmount')) {
        const index = e.target.name.match(/\d+/)[0];

        const totalCollectedValue =
          ((e.target.value ?? 0) * (fee.months?.value ?? 0)) / NUMBER_MONTHS_IN_YEAR;

        setInForm(`set_prepaids[${index}].totalCollected`, roundToDecimals(totalCollectedValue));
      }

      await handleChange(e, validationContext);
      await validateForm();
    },
    [fee.months?.value, handleChange, setInForm, validateForm],
  );

  if (fee.pendingChangesActionType === PendingChangesActionType.DELETED || isPageHidden) {
    return null;
  }

  return (
    <TableRow className={`table-row-pending-${fee.pendingChangesActionType}`}>
      <TableCell>
        {!fee.isLocallyAdded && (
          <>
            <TextFieldCDS
              disabled
              fullWidth
              InputProps={feeNameInputProps}
              dataTestId={LoanFeesTestIds.getPrepaidFeeTypeNameInputId(index)}
              error={!!_.get(vldErrors, `set_prepaids[${index}].feeTypeName`)}
              helperText={_.get(vldErrors, `set_prepaids[${index}].feeTypeName`)}
              id={`set_prepaids[${index}].feeTypeName`}
              isFieldChanged={isFieldValueChanged(fee, 'feeTypeName')}
              isHidden={hiddenFieldsMap.feeTypeName}
              name={`set_prepaids[${index}].feeTypeName`}
              size="small"
              value={fee.feeTypeName}
              onChange={handleChange}
            />
            <AprIndicator isIncludedInApr={countTowardsApr} />
          </>
        )}
        {fee.isLocallyAdded && (
          <>
            <AutocompleteCDS
              fullWidth
              withPopupError
              dataTestId={LoanFeesTestIds.getPrepaidFeeTypeSelectId(index)}
              disabled={isDisabledByLock || lockedFieldsMap.feeTypeName}
              error={!!_.get(vldErrors, `set_prepaids[${index}].feeTypeId`)}
              helperText={_.get(vldErrors, `set_prepaids[${index}].feeTypeId`)}
              id={`set_prepaids[${index}].feeTypeId`}
              isFieldChanged={isFieldValueChanged(fee, 'feeTypeId')}
              isHidden={hiddenFieldsMap.feeTypeId}
              name={`set_prepaids[${index}].feeTypeId`}
              options={filteredLoanFeesOptions}
              size="small"
              value={fee.feeTypeId}
              onChange={onFeeTypeNameChange(index)}
            />
            <AprIndicator isIncludedInApr={countTowardsApr} />
          </>
        )}
      </TableCell>

      <TableCell>
        <AutocompleteCDS
          fullWidth
          withPopupError
          dataTestId={LoanFeesTestIds.getPrepaidFeeProviderSelectId(index)}
          disabled={
            !isEditFeeProviderNameAllowed || isDisabledByLock || lockedFieldsMap.feeProviderId
          }
          error={!!_.get(vldErrors, `set_prepaids[${index}].feeProviderId`)}
          filterOptions={onFilterOptions}
          helperText={_.get(vldErrors, `set_prepaids[${index}].feeProviderId`)}
          id={`set_prepaids[${index}].feeProviderId`}
          isFieldChanged={isFieldValueChanged(fee, 'feeProviderId')}
          isHidden={hiddenFieldsMap.feeProviderId}
          name={`set_prepaids[${index}].feeProviderId`}
          options={feeProviderOptions}
          size="small"
          value={fee.feeProviderId}
          onChange={onChangeAutocomplete}
        />
      </TableCell>

      <TableCell align="center">
        <DataFieldCDS
          fullWidth
          withPopupError
          data-is-amount="true"
          disabled={isAnnualAmountDisabled}
          error={!!_.get(vldErrors, `set_prepaids[${index}].annualAmount`)}
          helperText={_.get(vldErrors, `set_prepaids[${index}].annualAmount`)}
          id={`set_prepaids[${index}].annualAmount`}
          isFieldChanged={isFieldValueChanged(fee, 'annualAmount')}
          isHidden={hiddenFieldsMap.annualAmount}
          name={`set_prepaids[${index}].annualAmount`}
          size="small"
          type={FieldInputType.CURRENCY}
          value={fee.annualAmount}
          onChange={onChangeAmountInput}
        />
      </TableCell>

      <TableCell>
        <AutocompleteCDS
          fullWidth
          withPopupError
          dataTestId={LoanFeesTestIds.getPrepaidMonthsSelectId(index)}
          disabled={isDisabledByLock || lockedFieldsMap.months}
          error={!!_.get(vldErrors, `set_prepaids[${index}].months`)}
          helperText={_.get(vldErrors, `set_prepaids[${index}].months`)}
          id={`set_prepaids[${index}].months`}
          isFieldChanged={isFieldValueChanged(fee, 'months')}
          isHidden={hiddenFieldsMap.months}
          name={`set_prepaids[${index}].months`}
          options={monthsWithZeroOptions}
          size="small"
          value={fee.months}
          onChange={onChangeAutocomplete}
        />
      </TableCell>

      <TableCell align="center" className="financed-amount-cell">
        <DataFieldCDS
          disabled
          fullWidth
          withPopupError
          error={!!_.get(vldErrors, `set_prepaids[${index}].totalCollected`)}
          helperText={_.get(vldErrors, `set_prepaids[${index}].totalCollected`)}
          id={`set_prepaids[${index}].totalCollected`}
          isFieldChanged={isFieldValueChanged(fee, 'totalCollected')}
          isHidden={hiddenFieldsMap.totalCollected}
          name={`set_prepaids[${index}].totalCollected`}
          size="small"
          type={FieldInputType.CURRENCY}
          value={fee.totalCollected}
        />
      </TableCell>

      <TableCell align="center">
        <IconButton
          disableRipple
          disableTouchRipple
          className="label-button"
          data-fee-id={feeId}
          data-testid={LoanFeesTestIds.getPrepaidDeleteButtonId(feeId)}
          disabled={isLoading}
          onClick={onDeleteFee}
        >
          <DeleteIcon size={IconSize[20]} />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

PrepaidTableRow.propTypes = {
  ...WithLoadingPropTypes,
  vldErrors: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  feeId: PropTypes.string.isRequired,
  handleChange: PropTypes.func.isRequired,
  validateField: PropTypes.func.isRequired,
  setInForm: PropTypes.func.isRequired,
  setForm: PropTypes.func.isRequired,
  pendingChangesActionType: PropTypes.string.isRequired,
  validateForm: PropTypes.func.isRequired,
  filteredLoanFeesOptions: PropTypes.array.isRequired,
  onDeleteFee: PropTypes.func.isRequired,
};

export default PrepaidTableRow;
